import React, { useState, useEffect } from 'react';
import Icon from '../../components/Icon';

export default function LORPowerRankings({ api }) {
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${api}/api/lor/power-rankings`)
      .then(r => r.json())
      .then(data => { setRankings(data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="lor-loading">Loading power rankings...</div>;

  const isPreSeason = rankings.length > 0 && rankings[0]?.season_mode === 'preseason';

  return (
    <div>
      <div className="lor-page-header">
        <h2 className="lor-page-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="⚡" size={18} /> Power Rankings</h2>
        <div className="lor-page-meta">
          {isPreSeason
            ? 'Pre-Season — 70% roster · 20% playoffs · 10% championship'
            : '30% roster · 20% form · 15% scoring · 10% record · 15% playoffs · 10% championship'}
        </div>
      </div>

      {isPreSeason && (
        <div className="lor-preseason-banner">
          <Icon emoji="⚡" size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Pre-season rankings. Form and scoring will factor in once games are played.
        </div>
      )}

      {rankings.length === 0 ? (
        <div className="lor-empty">Power rankings unavailable. Refresh after data loads.</div>
      ) : (
        <div className="lor-pr-cards">
          {rankings.map((r, i) => {
            const formPositive = (r.recent_margin || 0) > 5;
            const formNegative = (r.recent_margin || 0) < -5;
            return (
              <div key={r.roster_id} className="lor-card lor-pr-card">
                <div className="lor-pr-card-rank">
                  <span className="lor-pr-number">#{r.power_rank}</span>
                  {r.rank_change !== 0 && (
                    <span className={`lor-pr-change ${r.rank_change > 0 ? 'up' : 'down'}`}>
                      {r.rank_change > 0 ? `↑${r.rank_change}` : `↓${Math.abs(r.rank_change)}`}
                    </span>
                  )}
                </div>

                <div className="lor-pr-card-manager">
                  {r.avatar && (
                    <img
                      src={`https://sleepercdn.com/avatars/thumbs/${r.avatar}`}
                      alt={r.display_name}
                      className="lor-avatar lor-avatar--md"
                    />
                  )}
                  <div>
                    <div className="lor-pr-card-name">{r.display_name}</div>
                    <div className="lor-pr-card-record">{r.wins}–{r.losses} · {r.pf} PF</div>
                    <div style={{ color: 'var(--text3)', fontSize: '0.7rem', marginTop: 3 }}>
                      Projected {r.projected_record || '—'} · PO {Number(r.playoff_pct || 0).toFixed(0)}% · Title {Number(r.championship_pct || 0).toFixed(0)}%
                    </div>
                  </div>
                </div>

                <div className="lor-pr-card-bars">
                  <div className="lor-pr-bar-row">
                    <span className="lor-pr-bar-label">Score</span>
                    <div className="lor-pr-bar-track">
                      <div
                        className="lor-pr-bar-fill lor-pr-bar-fill--score"
                        style={{ width: `${Math.min(100, r.power_score)}%` }}
                      />
                    </div>
                    <span className="lor-pr-bar-val">{r.power_score?.toFixed(1)}</span>
                  </div>
                  {!isPreSeason && (
                    <div className="lor-pr-bar-row">
                      <span className="lor-pr-bar-label">Form</span>
                      <div className="lor-pr-bar-track">
                        <div
                          className={`lor-pr-bar-fill ${formPositive ? 'lor-pr-bar-fill--up' : formNegative ? 'lor-pr-bar-fill--down' : 'lor-pr-bar-fill--neutral'}`}
                          style={{ width: `${r.form_score || 50}%` }}
                        />
                      </div>
                      <span className="lor-pr-bar-val">
                        {(r.recent_margin || 0) > 0 ? '+' : ''}{(r.recent_margin || 0).toFixed(1)}
                      </span>
                    </div>
                  )}
                  <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8,
                    marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(var(--lor-accent-rgb), 0.12)',
                  }}>
                    <div>
                      <div style={{ color: 'var(--text3)', fontSize: '0.62rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Remaining SOS</div>
                      <div style={{ fontWeight: 700, marginTop: 3 }}>
                        {r.remaining_sos_rank ? `#${r.remaining_sos_rank} hardest` : 'Schedule unavailable'}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: 'var(--text3)', fontSize: '0.62rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Expected wins left</div>
                      <div style={{ fontWeight: 700, marginTop: 3 }}>{r.schedule_expected_wins ?? '—'}</div>
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <span style={{ color: 'var(--text3)', fontSize: '0.66rem' }}>Schedule played: </span>
                      <strong style={{ fontSize: '0.72rem' }}>
                        {r.played_sos_rank ? `#${r.played_sos_rank} hardest` : 'unavailable'}
                      </strong>
                    </div>
                    {r.hardest_remaining_opponent && (
                      <div style={{ gridColumn: '1 / -1', color: 'var(--text3)', fontSize: '0.7rem' }}>
                        Toughest remaining: <strong style={{ color: 'var(--text)' }}>{r.hardest_remaining_opponent}</strong>
                        {r.schedule_coverage_pct < 100 ? ` · ${r.schedule_coverage_pct}% schedule loaded` : ''}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="lor-pr-method-note">
        <strong>Methodology:</strong> Redraft history is excluded. Current lineup strength, the last three games,
        season scoring and record are blended with schedule-aware playoff and championship simulations.
        SOS ranks the average strength of each team&apos;s actual remaining opponents; #1 is hardest.
      </div>
    </div>
  );
}
